import React from 'react';
import { useNavigate } from 'react-router-dom';
import { EntityId } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { ColumnsType } from "antd/es/table";
import { destroyServerPlan } from "src/actions/mine_craft/serverPlansActions";
import { setEditId, ServerPlanInterface } from "src/store/mine_craft/serverPlansSlice";
import usePermissions from "src/hooks/usePermissions";
import { gigabytes, vcpu } from "./format";

import Table from "src/componets/Table";
import Swatch from "./_Swatch";
import { ButtonEdit, ButtonDestroy } from "src/componets/Buttons";
import { Space, Tag } from "antd";

interface ListTableInterface {
  className?: string;
}

const _ListTable = ({ className }: ListTableInterface) => {
  const navigate    = useNavigate();
  const dispatch    = useAppDispatch();
  const ids         = useAppSelector(state => state.mineCraft.serverPlans.ids);
  const serverPlans = useAppSelector(state => state.mineCraft.serverPlans.entities);
  const { write_minecraft } = usePermissions();
  const data = ids ? ids.map(id => serverPlans[id]) : [];

  function onClickEdit(id: EntityId) {
    dispatch(setEditId(id));
  }

  // A destroyed plan nullifies the reference on the servers it
  // provisioned — reload so their rows stop showing a dead plan.
  function onClickDestroy(id: number) {
    dispatch(destroyServerPlan(id)).then(() => navigate(0));
  }

  const columns: ColumnsType<ServerPlanInterface> = [
    {
      key: 'title',
      title: 'Plan',
      render: (_, { title, slug, featured }) => (
        <Space size='small'>
          <strong>{title}</strong>
          <code>{slug}</code>
          {featured && <Tag color='processing'>best value</Tag>}
        </Space>
      )
    },

    {
      key: 'coins',
      title: 'Price',
      render: (_, { coins }) => `${coins} coins`,
      width: 120
    },

    {
      key: 'memory_mb',
      title: 'Memory',
      render: (_, { memory_mb }) => gigabytes(memory_mb),
      width: 100
    },

    {
      key: 'disk_mb',
      title: 'Storage',
      render: (_, { disk_mb }) => gigabytes(disk_mb),
      width: 100
    },

    {
      key: 'cpu_percent',
      title: 'CPU',
      render: (_, { cpu_percent }) => vcpu(cpu_percent),
      width: 100
    },

    {
      key: 'max_players',
      title: 'Slots',
      render: (_, { max_players }) => max_players,
      width: 80
    },

    {
      key: 'colors',
      title: 'Colors',
      render: (_, { primary_color, tint_color }) => (
        <Space size='small'>
          <Swatch color={primary_color} title='Primary' />
          <Swatch color={tint_color} title='Tint' />
        </Space>
      ),
      width: 90
    },

    {
      key: 'published',
      title: 'Status',
      render: (_, { published }) => published
        ? <Tag color='success'>Published</Tag>
        : <Tag color='default'>Draft</Tag>,
      width: 110
    },

    ...(write_minecraft ? [{
      key: 'action',
      title: 'Action',
      render: (_: any, { id }: ServerPlanInterface) => (
        <Space size='middle'>
          <ButtonEdit key='button-edit' onClick={() => onClickEdit(id)} />
          <ButtonDestroy key='button-destroy' onClick={() => onClickDestroy(id)} />
        </Space>
      ),
      width: 120
    }] : []),
  ];

  return (
    <div className={className}>
      <Table
        rowKey='id'
        columns={columns}
        dataSource={data}
      />
    </div>
  )
}

export default _ListTable;
